import { useEffect, useRef, useState } from "react";
import { useTabDraft, useTabDrafts } from "@/components/ui/box-draft";
import type { Box, ListBox, TaskBox, Result, Blocker, FieldSave, FieldAria } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { gradebookApi, type GradeItem } from "@/lib/api/gradebook";
import { registerCommit, recoverRegisterCommit, recorded, registerCopy, keepResult, registerText, registerPhrases } from "@/lib/register-copy";
import type { Props } from "./AssessmentLines";
import { LineFields, AssessmentCell } from "./AssessmentLineParts";
import { Draft, validWeight } from "./assessment-line-draft";

const blank: Draft = {
  label: "",
  type: "assignment",
  weight: "",
  remove: false,
};

export function useAssessmentBoxes({
  courseId,
  items,
  scoredCounts,
  frozenReason,
  onChanged,
  read,
  version,
  onEditingChange,
}: Props) {
  const [fieldSaves, setFieldSaves] = useState<Record<string, FieldSave>>({});
  const [results, setResults] = useState<Record<string, Result>>({});
  const createKeep = useTabDraft<Draft, string>({ box: "assessment-add", record: courseId, version: version ?? null, initial: blank });
  const ofLine = useTabDrafts<Draft, string>("assessment-lines");
  const keeps = new Map(items.map(item => [item.id, ofLine({ record: `${courseId}/${item.id}`, version: item.updated_at ?? null,
    initial: { label: item.label, type: item.type, weight: String(item.weight), remove: false } })]));
  const keepFor = (id: string) => id === "new" ? createKeep : keeps.get(id)!;
  const newLine = { ...createKeep.value, version: createKeep.began ?? undefined };
  const setNewLine = (next: Draft | ((held: Draft) => Draft)) => {
    createKeep.set(held => { const { version: _version, ...value } = typeof next === "function" ? next(held) : next; return value; });
  };
  const confirmed = useRef<Result | null>(null);
  const [dirtyCells, setDirtyCells] = useState<Record<string, boolean>>({});
  const editing = createKeep.kept || [...keeps.values()].some(keep => keep.kept) || Object.values(dirtyCells).some(Boolean);
  useEffect(() => { onEditingChange?.(editing); }, [editing, onEditingChange]);
  const draftFor = (item: GradeItem) => ({ ...keepFor(item.id).value, version: keepFor(item.id).began ?? undefined });
  const resultFor = (id: string): Result => {
    if (results[id]) return results[id];
    if (!keepFor(id).pending) return { state: "none" };
    const restored = recoverRegisterCommit<GradeItem | undefined>(keepFor(id).pending!, async (saved) => {
      await onChanged();
      return recorded([registerText.assessmentLineRecorded], id === "new" ? saved?.course_updated_at : saved?.updated_at);
    });
    return restored.state === "unknown" ? { ...restored, check: { ...restored.check, run: async () => store(id, await restored.check.run()) } } : restored;
  };
  const store = (id: string, result: Result) => {
    return keepResult(result, (value) =>
      { setResults((r) => ({ ...r, [id]: value }));
        if (value.state === "confirmed" || value.state === "refused") keepFor(id).setPending(null);
      },
    );
  };
  const blockers = (
    draft: Draft,
    part: string,
    item?: GradeItem,
  ): Blocker[] => {
    if (frozenReason)
      return [{ kind: "missing", part, sentence: frozenReason }];
    if (!version || (read && read.status !== "ready"))
      return [
        {
          kind: "missing",
          part,
          sentence: registerText.readTheLatestAssessmentLinesBeforeChangingThem,
        },
      ];
    if (draft.remove)
      return item && (scoredCounts[item.id] ?? 0) > 0
        ? [
            {
              kind: "missing",
              part,
              sentence:
                registerText.recordedScoresReferToThisLineKeepItAndSet,
            },
          ]
        : [];
    if (!draft.label.trim())
      return [{ kind: "missing", part, sentence: registerText.nameTheAssessmentLine }];
    if (!validWeight(draft.weight))
      return [
        {
          kind: "missing",
          part,
          sentence: registerText.weightMustBeAWholeNumberFrom0To100,
        },
      ];
    return [];
  };
  const save = async (key: string, draft: Draft, item?: GradeItem) => {
    const id = item?.id ?? "new";
    keepFor(id).setPending(key);
    const result = await registerCommit(
      key,
      async () => {
        try {
        const write = { key, version: draft.version! };
        if (draft.remove && item) {
          await gradebookApi.deleteGradeItem(courseId, item.id, write);
          return undefined;
        }
        const body = {
          label: draft.label.trim(),
          type: draft.type,
          weight: Number(draft.weight),
        };
        if (item)
          return await gradebookApi.updateGradeItem(courseId, item.id, body, write);
        else
          return await gradebookApi.createGradeItem(
            courseId,
            {
              ...body,
              sort_order:
                Math.max(-1, ...items.map((line) => line.sort_order)) + 1,
            },
            write,
          );
        } catch (error) {
          if (error instanceof ApiError && error.status === 409) await onChanged();
          throw error;
        }
      },
      async (saved) => {
        await onChanged();
        return recorded([
          draft.remove
            ? registerText.assessmentLineRemoved
            : item
              ? registerText.assessmentLineUpdated
              : registerText.assessmentLineAdded,
        ], item ? saved?.updated_at : saved?.course_updated_at);
      },
    );
    if (!item && result.state === "confirmed") confirmed.current = result;
    return store(id, result);
  };
  const writer =
    (lines: GradeItem[], line: GradeItem, field: "weight" | "order") =>
    async (value: string, key: string, token: string) => {
      if (field === "weight")
        return gradebookApi.updateGradeItem(
          courseId,
          line.id,
          { weight: Number(value) },
          { key, version: token },
        );
      const ordered = [...lines].sort((a, b) => a.sort_order - b.sort_order);
      const next = ordered.filter((item) => item.id !== line.id);
      next.splice(Number(value) - 1, 0, line);
      return gradebookApi.reorderGradeItems(
        courseId,
        next.map((item, index) => ({
          id: item.id,
          expected_sort_order: item.sort_order,
          sort_order: index,
        })),
        { key, version: token },
      );
    };
  const list: ListBox<GradeItem[], GradeItem> = {
    kind: "list",
    id: "assessment-lines",
    job: registerText.defineTheBasisOfTheWorkingResult,
    label: registerText.assessmentLines,
    owner: { system: "imam" },
    read: read ?? {
      status: "ready",
      value: items,
      readAt: new Date().toISOString(),
    },
    fold: "never",
    detail: (lines) =>
      lines.length
        ? registerText.eachLineCarriesAShareOfTheCourseResult
        : registerText.noAssessmentLinesYet,
    state: (lines) => ({
      class:
        lines.reduce((sum, line) => sum + line.weight, 0) === 100
          ? "done"
          : "waiting",
      word: registerPhrases.totalWeight(lines.reduce((sum, line) => sum + line.weight, 0)),
    }),
    figures: (lines) => [{ value: lines.length, unit: registerText.lines }],
    rows: (lines) => lines,
    table: {
      title: registerText.assessment,
      columns: [
        { id: "type", label: registerText.type },
        { id: "weight", label: registerText.weight },
        { id: "order", label: registerText.position },
      ],
      note: registerText.aFieldSavesWhenYouLeaveItEscapeRestoresIts,
    },
    draft: [...keeps.values()].some(keep => keep.kept),
    candidate: { eligible: !frozenReason, blocked: !version || (read && read.status !== "ready") ? { kind: "missing", part: "new-line", sentence: registerText.readTheLatestAssessmentLinesBeforeChangingThem } : null, rank: null },
    row: {
      key: (line) => line.id,
      title: (line) => line.label,
      cells: (line) => [
        { id: "type", read: registerCopy.lineTypes[line.type] },
        ...(["weight", "order"] as const).map((field) => {
          const value =
            field === "weight"
              ? String(line.weight)
              : String(
                  [...items]
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .findIndex((item) => item.id === line.id) + 1,
                );
          const token = field === "weight" ? line.updated_at : version;
          if (frozenReason) return { id: field, read: value };
          return {
            id: field,
            save: fieldSaves[line.id + "/" + field] ?? {
              status: "idle" as const,
            },
            render: (label: string, aria: FieldAria) => (
              <AssessmentCell
                label={label}
                aria={aria}
                lastRow={line.id === items[items.length - 1]?.id}
                onDirty={(dirty) => setDirtyCells((held) => held[line.id + "/" + field] === dirty ? held : { ...held, [line.id + "/" + field]: dirty })}
                value={value}
                version={token}
                write={writer(items, line, field)}
                refresh={onChanged}
                onSave={(value) =>
                  setFieldSaves((held) => ({
                    ...held,
                    [line.id + "/" + field]: value,
                  }))
                }
                valid={(value) =>
                  field === "weight"
                    ? validWeight(value)
                    : Number.isInteger(Number(value)) &&
                      Number(value) >= 1 &&
                      Number(value) <= items.length
                }
                readNewer={async () => {
                  const [lines, grid] = await Promise.all([
                    gradebookApi.getGradeItems(courseId),
                    gradebookApi.getGrades(courseId),
                  ]);
                  const newer = lines.find((item) => item.id === line.id);
                  if (!newer)
                    throw new Error(registerText.thisAssessmentLineIsUnavailable);
                  return {
                    value:
                      field === "weight"
                        ? String(newer.weight)
                        : String(
                            [...lines]
                              .sort((a, b) => a.sort_order - b.sort_order)
                              .findIndex((item) => item.id === line.id) + 1,
                          ),
                    version:
                      field === "weight" ? newer.updated_at : grid.updated_at,
                    write: writer(lines, newer, field),
                  };
                }}
              />
            ),
          };
        }),
      ],
      state: () => null,
      act: () => ({ verb: registerText.changeLine, does: "open" }),
      task: (line) => {
        const draft = draftFor(line);
        const keep = keepFor(line.id);
        const conflict = Boolean(keep.conflict);
        return {
          result: resultFor(line.id),
          parts: [{ id: line.id, label: registerText.assessment, state: { class: "draft", word: registerText.notRecorded }, owner: { system: "imam" }, fold: "never", draft: "browser", keep,
          render: () => (
            <div className="grid gap-3">
              {conflict && <ConflictNotice object={registerText.assessmentLine} message={registerText.thisLineChangedYourDraftIsKept}
                facts={[{ label: registerText.currentName, theirs: line.label }, { label: registerText.currentWeight, theirs: String(line.weight) }]}
                keepMineLabel={registerText.keepMyDraftOnTheNewerLine} loadTheirsLabel={registerText.loadTheNewerLine}
                onKeepMine={keep.keepMine}
                onLoadTheirs={keep.loadNewer}
              />}
              <fieldset disabled={Boolean(frozenReason) || resultFor(line.id).state === "unknown"}>
              <LineFields
                id={line.id}
                draft={draft}
                set={(value) => {
                  const { version: _version, ...fields } = value; keep.set(fields);
                  setResults((r) => ({ ...r, [line.id]: { state: "none" } }));
                }}
              />
              <label className="text-ui">
                <input
                  type="checkbox"
                  checked={draft.remove}
                  onChange={(e) =>
                    keep.set(held => ({ ...held, remove: e.target.checked }))
                  }
                />{" "}
                {registerText.removeThisLine}</label>
              </fieldset>
            </div>
          ), }],
          commit: {
            verb: draft.remove ? registerText.removeLine : registerText.saveLine,
            cap: registerText.whenYouChangeThisLine,
            effects: () => [
              {
                id: line.id,
                sentence: draft.remove
                  ? registerText.removeThisLineFromTheWorkingResult
                  : registerPhrases.lineEffect(draft.label.trim() || registerText.unnamedLine, draft.weight),
              },
            ],
            blockers: () => conflict ? [{ kind: "conflict", part: line.id, sentence: registerText.chooseHowToUseTheNewerAssessmentLine }] : blockers(draft, line.id, line),
            revises: {
              part: line.id,
              unchanged: () =>
                !draft.remove &&
                draft.label === line.label &&
                draft.type === line.type &&
                draft.weight === String(line.weight),
            },
            run: (key) => save(key, draft, line),
          },
        };
      },
    },
  };
  const create: TaskBox<GradeItem[]> = {
    kind: "task",
    id: "add-assessment-line",
    job: registerText.addOneAssessmentLine,
    label: registerText.addALine,
    owner: { system: "imam" },
    read: list.read,
    fold: "choice",
    detail: () => registerText.nameTheAssessmentAndItsShareOfTheWorkingResult,
    state: () => ({
      class: resultFor("new").state === "confirmed" ? "done" : newLine.label || newLine.weight ? "draft" : "waiting",
      word: resultFor("new").state === "confirmed" ? registerText.lineAdded : registerText.newAssessmentLine,
    }),
    exits: resultFor("new").state === "confirmed" ? [{ label: registerText.addAnotherLine, reopens: true }] : undefined,
    act: resultFor("new").state === "unknown" ? { does: "check", verb: registerText.check } : { does: "open", verb: registerText.addALine },
    candidate: { eligible: !frozenReason, blocked: !version || (read && read.status !== "ready") ? { kind: "missing", part: "new-line", sentence: registerText.readTheLatestAssessmentLinesBeforeChangingThem } : null, rank: null },
    draft: Boolean(newLine.label || newLine.weight),
    result: resultFor("new"),
    parts: [
      {
        id: "new-line",
        label: registerText.assessment,
        state: { class: "draft", word: registerText.notAdded },
        markedByBox: true,
        owner: { system: "imam" },
        fold: "never",
        draft: "browser",
        keep: createKeep,
        render: () => (
          <>
          {newLine.version && newLine.version !== version && <ConflictNotice object={registerText.assessmentLines2} message={registerText.theAssessmentLinesChangedYourNewLineIsKept}
            facts={[{ label: registerText.currentTotal, theirs: String(items.reduce((sum, line) => sum + line.weight, 0)) + "%" }]}
            keepMineLabel={registerText.keepMyNewLineOnTheNewerBasis} loadTheirsLabel={registerText.loadTheNewerBasis}
            onKeepMine={createKeep.keepMine}
            onLoadTheirs={createKeep.loadNewer} />}
          <fieldset disabled={!version || Boolean(frozenReason) || resultFor("new").state === "unknown"}>
          <LineFields
            id="new-line"
            draft={newLine}
            set={(value) => {
              setNewLine({ ...value, version: newLine.version ?? version });
              store("new", { state: "none" });
            }}
          />
          </fieldset>
          </>
        ),
      },
    ],
    commit: {
      verb: registerText.addLine,
      cap: registerText.whenYouAddTheLine,
      effects: () => [
        {
          id: "new-line",
          sentence: registerPhrases.lineEffect(newLine.label.trim() || registerText.newAssessment, newLine.weight),
        },
      ],
      blockers: () => newLine.version && newLine.version !== version ? [{ kind: "conflict", part: "new-line", sentence: registerText.chooseHowToUseTheNewerAssessmentBasis }] : blockers(newLine, "new-line"),
      run: (key) => save(key, newLine),
    },
  };
  return { boxes: [list as Box, create as Box], onOpenChange: (id: string, open: boolean) => {
    if (id !== create.id) return;
    if (open && resultFor("new").state === "confirmed") store("new", { state: "none" });
    else if (!open && !newLine.label && !newLine.weight && resultFor("new").state === "none" && confirmed.current) store("new", confirmed.current);
  } };
}
